import { useEffect, useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useConfirm } from '../components/ConfirmProvider'

/**
 * Bewaak niet-opgeslagen wijzigingen.
 *
 * - `useUnsavedChanges(isDirty)` registreert een `beforeunload`-waarschuwing zolang
 *   er onbewaarde wijzigingen zijn (browser sluiten / verversen / tab sluiten).
 * - `confirmDiscardIfDirty(isDirty)` geeft true terug als er veilig gesloten mag
 *   worden (niet dirty, of de gebruiker bevestigt het weggooien). Gebruik dit in
 *   sluit-/annuleer-handlers van modals en formulieren.
 *
 * Bewust geen react-router `useBlocker`: de app draait op een gewone BrowserRouter
 * (geen data-router), waar useBlocker niet beschikbaar is.
 */
export const UNSAVED_DISCARD_MESSAGE =
  'Er zijn niet-opgeslagen wijzigingen. Wil je sluiten zonder te bewaren?'

export function useUnsavedChanges(isDirty, { message = UNSAVED_DISCARD_MESSAGE } = {}) {
  useEffect(() => {
    if (!isDirty) return undefined
    const handler = (e) => {
      e.preventDefault()
      // Moderne browsers tonen een generieke melding; returnValue is vereist.
      e.returnValue = message
      return message
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty, message])
}

/** True als er gesloten mag worden (niet dirty, of gebruiker bevestigt). Synchrone
 *  fallback (window.confirm) — gebruik bij voorkeur useDiscardGuard voor de R11-dialoog. */
export function confirmDiscardIfDirty(isDirty, message = UNSAVED_DISCARD_MESSAGE) {
  if (!isDirty) return true
  return window.confirm(message)
}

/**
 * Async discard-guard met de gestileerde R11-bevestiging.
 *   const guard = useDiscardGuard()
 *   onClick={async () => { if (await guard(isDirty)) doClose() }}
 * Valt terug op window.confirm als er geen ConfirmProvider is.
 */
export function useDiscardGuard() {
  const confirm = useConfirm()
  const { t } = useTranslation()
  return useCallback(async (isDirty) => {
    if (!isDirty) return true
    if (!confirm) return window.confirm(UNSAVED_DISCARD_MESSAGE)
    return confirm({
      title: t('common.unsavedTitle', { defaultValue: 'Niet-opgeslagen wijzigingen' }),
      message: t('common.unsavedBody', { defaultValue: 'Je hebt wijzigingen die nog niet bewaard zijn. Wil je sluiten zonder te bewaren?' }),
      confirmText: t('common.discardClose', { defaultValue: 'Sluiten zonder bewaren' }),
      cancelText: t('common.keepEditing', { defaultValue: 'Blijven bewerken' }),
      danger: true,
    })
  }, [confirm, t])
}

/**
 * Nauwkeurige dirty-detectie voor een (inline) formulier: legt een snapshot vast
 * wanneer `open` true wordt, en vergelijkt daarna met de huidige `value`.
 *   const isDirty = useDirtySnapshot(showForm, formData)
 * Geen valse waarschuwing als de gebruiker een wijziging weer terugzet.
 */
export function useDirtySnapshot(open, value) {
  const snapshot = useRef(null)
  useEffect(() => {
    snapshot.current = open ? JSON.stringify(value) : null
    // Bewust alleen op `open`: vastleggen bij openen, niet bij elke wijziging.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  return !!open && snapshot.current != null && JSON.stringify(value) !== snapshot.current
}

/**
 * Touched-heuristiek voor een eigen modal (zonder gedeelde Dialog): markeert
 * "aangeraakt" bij elke veldwijziging, reset bij (her)openen, en levert een
 * async sluit-bevestiging (R11). Plus beforeunload zolang touched.
 *
 *   const guard = useTouchedGuard(isOpen)
 *   // op de modal-content:  onChange={guard.onContentChange}
 *   // op X/overlay/annuleer: onClick={() => guard.closeWith(onClose)}
 */
export function useTouchedGuard(open) {
  const [touched, setTouched] = useState(false)
  useEffect(() => { if (open) setTouched(false) }, [open])
  useUnsavedChanges(!!open && touched)
  const discard = useDiscardGuard()
  const onContentChange = useCallback(() => setTouched(true), [])
  const closeWith = useCallback(async (onClose) => {
    if (await discard(touched)) { onClose?.(); return true }
    return false
  }, [discard, touched])
  return { touched, onContentChange, closeWith }
}

export default useUnsavedChanges
